import { useCallback, useState, type MouseEvent as ReactMouseEvent, type ReactElement } from 'react';
import { AppContextMenu } from './AppContextMenu';
import type { MenuAction } from '@/components/Menu/Actions';

/** Nudge anchor so the menu aligns with prior native-context-menu positioning. */
const CONTEXT_MENU_ANCHOR_OFFSET_X = 2;
const CONTEXT_MENU_ANCHOR_OFFSET_Y = 4;
const CONTEXT_MENU_WIDTH = '160px';

export interface UseAppContextMenuOptions {
  items: MenuAction[];
}

export interface UseAppContextMenuResult {
  handleContextMenuOpen: (event: ReactMouseEvent<HTMLElement>) => void;
  contextMenu: ReactElement;
}

export function useAppContextMenu({ items }: UseAppContextMenuOptions): UseAppContextMenuResult {
  const [{ isOpen, mouseX, mouseY }, setMenuState] = useState<{
    isOpen: boolean;
    mouseX: number | null;
    mouseY: number | null;
  }>({
    isOpen: false,
    mouseX: null,
    mouseY: null,
  });

  const handleClose = useCallback((_event: ReactMouseEvent) => {
    setMenuState({
      isOpen: false,
      mouseX: null,
      mouseY: null,
    });
  }, []);

  const handleContextMenuOpen = useCallback((event: ReactMouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setMenuState({
      isOpen: true,
      mouseX: event.clientX - CONTEXT_MENU_ANCHOR_OFFSET_X,
      mouseY: event.clientY - CONTEXT_MENU_ANCHOR_OFFSET_Y,
    });
  }, []);

  const contextMenu = (
    <AppContextMenu
      open={isOpen}
      onClose={handleClose}
      mouseX={mouseX}
      mouseY={mouseY}
      items={items}
      width={CONTEXT_MENU_WIDTH}
    />
  );

  return {
    handleContextMenuOpen,
    contextMenu,
  };
}
